
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import { GroupMember, Channel } from "@/lib/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { handleError } from "@/utils/error-handler";
import { toUUID, toDatabaseValue } from "@/utils/supabase";

type ChatInputProps = {
  channelId: string;
  userId: string;
  currentMember: GroupMember | null;
  channel: Channel;
};

export const ChatInput = ({ channelId, userId, currentMember, channel }: ChatInputProps) => {
  const [messageInput, setMessageInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const handleSendMessage = async () => {
    if (!userId || !messageInput.trim() || !channelId) return;
    
    // Validate message length
    if (messageInput.length > 2000) {
      toast.error("Message cannot exceed 2000 characters");
      return;
    }
    
    // Check if user is muted
    if (currentMember?.isMuted) {
      toast.error("You are currently muted in this group");
      return;
    }
    
    // Check permissions for announcement channels
    if (channel?.isAnnouncement && 
        currentMember?.role !== 'admin' && 
        currentMember?.role !== 'moderator') {
      toast.error("Only admins and moderators can post in announcement channels");
      return;
    }

    try {
      setSendingMessage(true);
      
      const messageData = toDatabaseValue({
        channel_id: toUUID(channelId),
        user_id: toUUID(userId),
        content: messageInput.trim()
      });

      const { error } = await supabase
        .from("messages")
        .insert(messageData);

      if (error) {
        console.error("Error inserting message:", error);
        throw error;
      }

      // Clear input field after successful send
      setMessageInput("");
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(handleError(error, "Failed to send message"));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (channel?.isAnnouncement && 
      currentMember?.role !== 'admin' && 
      currentMember?.role !== 'moderator') {
    return (
      <div className="bg-muted p-4 text-center rounded-md">
        <p>Only admins and moderators can post in announcement channels</p>
      </div>
    );
  }
  
  if (currentMember?.isMuted) {
    return (
      <div className="bg-muted p-4 text-center rounded-md">
        <p>You are currently muted in this group</p>
      </div>
    );
  }

  return (
    <div className="flex space-x-2">
      <Textarea
        value={messageInput}
        onChange={(e) => setMessageInput(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder="Type your message..."
        className="resize-none"
        rows={2}
      />
      <Button 
        onClick={handleSendMessage} 
        disabled={!messageInput.trim() || sendingMessage}
        className="self-end"
      >
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
};
